import React, { createContext, useContext, useState } from 'react';

export type Lang = 'en' | 'ar';

const translations = {
  en: {
    // Sidebar
    agent: 'Agent',
    allAgents: 'All Agents',
    environment: 'Environment',
    health: 'Health',
    education: 'Education',
    economics: 'Economics',
    comingSoon: 'Coming soon',
    search: 'Search',
    recent: 'Recent',
    newChat: 'New Chat',
    // Source picker
    source: 'Report',
    sourceChip: 'Source',
    selectReport: 'Select a report',
    sourcePlaceholder: 'Focus on one report…',
    noReports: 'No reports found',
    focusedOn: 'Focused on',
    clearSource: 'Clear',
    // Input
    inputPlaceholder: 'Ask about economy, technology, health, or other reports…',
    inputPlaceholderFocused: 'Ask about “{title}”…',
    // Welcome
    welcomeTitle: 'Welcome to Enmaeya chatbot',
    welcomeSub: 'Your gateway to news, articles, and resources across economy, technology, health, and more.',
    // Search
    searchPlaceholder: 'Search conversations…',
  },
  ar: {
    // Sidebar
    agent: 'الوكيل',
    allAgents: 'جميع الوكلاء',
    environment: 'البيئة',
    health: 'الصحة',
    education: 'التعليم',
    economics: 'الاقتصاد',
    comingSoon: 'قريباً',
    search: 'بحث',
    recent: 'الأخيرة',
    newChat: 'محادثة جديدة',
    // Source picker
    source: 'تقرير',
    sourceChip: 'المصدر',
    selectReport: 'اختر تقريرًا',
    sourcePlaceholder: 'ركّز على تقرير محدد…',
    noReports: 'لا توجد تقارير',
    focusedOn: 'التركيز على',
    clearSource: 'مسح',
    // Input
    inputPlaceholder: 'اسأل عن الاقتصاد، التكنولوجيا، الصحة، أو تقارير أخرى…',
    inputPlaceholderFocused: 'اسأل عن «{title}»…',
    // Welcome
    welcomeTitle: 'مرحباً بك في روبوت إنماءية',
    welcomeSub: 'بوابتك للأخبار والمقالات والموارد في الاقتصاد والتكنولوجيا والصحة وغيرها.',
    // Search
    searchPlaceholder: 'ابحث في المحادثات…',
  },
} as const;

type Translations = { [K in keyof typeof translations.en]: string };

interface LanguageContextValue {
  lang: Lang;
  t: Translations;
  toggleLang: () => void;
  isRTL: boolean;
}

export const LanguageContext = createContext<LanguageContextValue>({
  lang: 'en',
  t: translations.en as Translations,
  toggleLang: () => {},
  isRTL: false,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Lang>('en');
  const toggleLang = () => setLang(l => (l === 'en' ? 'ar' : 'en'));

  return (
    <LanguageContext.Provider value={{ lang, t: translations[lang] as Translations, toggleLang, isRTL: lang === 'ar' }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
